import { createSystem, defaultConfig, defineConfig, defineRecipe } from "@chakra-ui/react";

// ---------------------------------------------------------------------------
// GLOBAL "Soft Modern" button — every <Button> in the app inherits this.
// Palette unchanged: teal #25A1B0 / dark teal #0E7C8B / tint #E6F5F7 /
// hairline #BFE3E8 / slate #1F2937 / white / poppy #D9482B.
// ---------------------------------------------------------------------------
const buttonRecipe = defineRecipe({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: "600",
    lineHeight: "1",
    borderRadius: "11px",
    cursor: "pointer",
    flexShrink: 0,
    transitionProperty: "background-color, border-color, color, box-shadow, opacity",
    transitionDuration: "0.15s",
    transitionTimingFunction: "ease",
    _focusVisible: { outline: "none", boxShadow: "0 0 0 3px #BFE3E8" },
    _disabled: { opacity: 0.45, cursor: "not-allowed", boxShadow: "none" },
  },
  variants: {
    size: {
      // Small — table rows & toolbars.
      xs: { minH: "40px", h: "auto", px: "18px", fontSize: "13.5px" },
      sm: { minH: "40px", h: "auto", px: "18px", fontSize: "13.5px" },
      // Regular — everything else.
      md: { minH: "48px", h: "auto", px: "26px", fontSize: "15px" },
      lg: { minH: "48px", h: "auto", px: "26px", fontSize: "15px" },
    },
    variant: {
      // Primary — ONE per screen.
      solid: {
        bg: "#25A1B0",
        color: "#FFFFFF",
        boxShadow: "0 2px 5px rgba(37,161,176,0.28)",
        _hover: { bg: "#0E7C8B", _disabled: { bg: "#25A1B0" } },
      },
      // Secondary — supporting actions.
      outline: {
        bg: "#FFFFFF",
        color: "#0E7C8B",
        borderWidth: "1.5px",
        borderStyle: "solid",
        borderColor: "#BFE3E8",
        _hover: { bg: "#E6F5F7", _disabled: { bg: "#FFFFFF" } },
      },
      // Ghost / text — low-priority actions.
      ghost: {
        bg: "transparent",
        color: "#0E7C8B",
        _hover: { bg: "#E6F5F7", _disabled: { bg: "transparent" } },
      },
      // Danger — destructive primaries (Delete).
      danger: {
        bg: "#D9482B",
        color: "#FFFFFF",
        boxShadow: "0 2px 5px rgba(217,72,43,0.28)",
        _hover: { bg: "#B93A21", _disabled: { bg: "#D9482B" } },
      },
    },
  },
  defaultVariants: { size: "md", variant: "solid" },
});

const config = defineConfig({
  theme: {
    tokens: {
      fonts: {
        heading: { value: "'Montserrat', sans-serif" },
        body: { value: "'Inter', sans-serif" },
      },
    },
    recipes: {
      button: buttonRecipe,
    },
  },
  globalCss: {
    "html, body, #root": {
      bg: "#F7F9FA",
      color: "#1F2937",
      fontFamily: "'Inter', sans-serif",
    },
  },
});

export const system = createSystem(defaultConfig, config);
